import { mobileBreakpoint, tabletBreakpoint, desktopBreakpoint, wideScreenBreakpoint } from 'react-components'
import type { ImagePreloadLink } from '../../../common/types'
import type { BackgroundImages } from './styles'
import type { HeroSectionTransport } from '../../../transports/region-landing-page-transport'
import { getImageUrl } from '../../landing-page/utils'
import { DEFAULT_REGION_BACKGROUND_IMAGES } from './constants'

// Breakpoint values from react-components:
// mobile: max: 767 (no min)
// tablet: min: 768, max: 1023
// desktop: min: 1024 (no max)
// wideScreen: min: 1276
const MOBILE_MAX = mobileBreakpoint?.max ?? 767
const TABLET_MIN = tabletBreakpoint?.min ?? 768
const TABLET_MAX = tabletBreakpoint?.max ?? 1023
const DESKTOP_MIN = desktopBreakpoint?.min ?? 1024
const WIDESCREEN_MIN = wideScreenBreakpoint?.min ?? 1276

/**
 * Converts HeroSectionTransport to BackgroundImages object.
 * Useful for pages like region.tsx where hero images are dynamic from API.
 *
 * @param hero - HeroSectionTransport from API response
 * @returns BackgroundImages object that can be used with CommonHeroSection and generateHeroImagePreloadLinks
 */
export const heroSectionToBackgroundImages = (hero: HeroSectionTransport): BackgroundImages => {
    const { slide } = hero

    if (!slide?.image) {
        return DEFAULT_REGION_BACKGROUND_IMAGES
    }

    return {
        smallMobile: getImageUrl('small-mobile', slide) ?? DEFAULT_REGION_BACKGROUND_IMAGES.smallMobile,
        mobile: getImageUrl('medium-mobile', slide) ?? DEFAULT_REGION_BACKGROUND_IMAGES.mobile,
        tablet: getImageUrl('large-mobile', slide) ?? DEFAULT_REGION_BACKGROUND_IMAGES.tablet,
        desktop: getImageUrl('desktop', slide) ?? DEFAULT_REGION_BACKGROUND_IMAGES.desktop,
        wideScreen: getImageUrl('wide-screen', slide) ?? DEFAULT_REGION_BACKGROUND_IMAGES.wideScreen,
    }
}

/**
 * Generates preload links for hero background images.
 * Media queries are mutually exclusive ranges matching CSS breakpoints.
 *
 * Breakpoint ranges:
 * - smallMobile: 0 - 479px (very small screens)
 * - mobile: 480px - 767px
 * - tablet: 768px - 1023px
 * - desktop: 1024px - 1275px
 * - wideScreen: 1276px+
 *
 * @param backgroundImages - The same BackgroundImages object passed to CommonHeroSection
 * @returns ImagePreloadLink[] to be passed to Page component's imagePreloadLinks prop
 */
export const generateHeroImagePreloadLinks = (backgroundImages: BackgroundImages): ImagePreloadLink[] => {
    const preloadLinks: ImagePreloadLink[] = []

    // Small Mobile (0 - 479px)
    if (backgroundImages.smallMobile) {
        preloadLinks.push({
            href: backgroundImages.smallMobile,
            fetchPriority: 'high',
            media: `(max-width: 479px)`,
        })
    }

    // Mobile (480px - 767px)
    if (backgroundImages.mobile) {
        preloadLinks.push({
            href: backgroundImages.mobile,
            fetchPriority: 'high',
            media: `(min-width: 480px) and (max-width: ${MOBILE_MAX}px)`,
        })
    }

    // Tablet (768px - 1023px)
    if (backgroundImages.tablet) {
        preloadLinks.push({
            href: backgroundImages.tablet,
            fetchPriority: 'high',
            media: `(min-width: ${TABLET_MIN}px) and (max-width: ${TABLET_MAX}px)`,
        })
    }

    // Desktop (1024px - 1275px)
    if (backgroundImages.desktop) {
        preloadLinks.push({
            href: backgroundImages.desktop,
            fetchPriority: 'high',
            media: `(min-width: ${DESKTOP_MIN}px) and (max-width: ${WIDESCREEN_MIN - 1}px)`,
        })
    }

    // Wide Screen (1276px and above)
    if (backgroundImages.wideScreen) {
        preloadLinks.push({
            href: backgroundImages.wideScreen,
            fetchPriority: 'high',
            media: `(min-width: ${WIDESCREEN_MIN}px)`,
        })
    }

    return preloadLinks
}
